import React, { Fragment, useEffect, useState } from 'react';
import { RefreshControl, ScrollView, View, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Card from 'src/presentation/components/atoms/Card';
import Cta from 'src/presentation/components/atoms/Cta';
import Heading from 'src/presentation/components/atoms/Heading';
import Loader from 'src/presentation/components/atoms/Loader';
import Paragraph from 'src/presentation/components/atoms/Paragraph';
import AddEvent from 'src/presentation/components/organisms/AddEvent';
import EventGrid from 'src/presentation/components/organisms/EventGrid';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Constants from 'src/core/utils/Constants';
import { usePendingEventDeeplink } from 'src/presentation/hooks/usePendingEventDeeplink';

function HomeScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const eventStore = useEventStore();
	const authStore = useAuthenticationStore();
	const [isBooting, setIsBooting] = useState(true);

	// After login via an event deeplink: show the confirmation / open the event here.
	usePendingEventDeeplink();

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		async function boot() {
			await Promise.all([authStore.getProfile(), eventStore.init()]);
			setIsBooting(false);
		}
		boot().catch(() => {
			setIsBooting(false);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [refreshing, setRefreshing] = useState(false);

	const onRefresh = () => {
		setRefreshing(true);
		eventStore
			.refreshAllEvents()
			.catch(() => {
				// handled by store
			})
			.finally(() => {
				setRefreshing(false);
			});
	};

	const firstname = authStore.userProfile?.firstname;
	const welcomeTitle = authStore.isLoggedin
		? firstname
			? Translations.successWelcomeNamed(firstname)
			: Translations.successWelcomeDefault
		: Translations.titleWelcome;

	// Logged out with nothing added yet: lead with a login call-to-action so it is clear you log in first, then
	// land on the add-events placeholder once signed in.
	const showLoginCta = !authStore.isLoggedin && eventStore.eventData.length === 0;

	// Full-screen loader on boot, and after login while account-linked events are still being fetched (eventData
	// empty).
	if (isBooting || (eventStore.isLoading && eventStore.eventData.length === 0)) {
		return (
			<Fragment>
				<NavBar
					backBtn={false}
					bgcolor={Colors.primary}
					centerTitle={true}
					color={Colors.black}
					editBtn={false}
					searchBtn={false}
					statusBarStyle="dark-content"
					title="Topicus Events"
				/>
				<Loader />
			</Fragment>
		);
	}

	return (
		<Fragment>
			<NavBar
				backBtn={false}
				bgcolor={Colors.primary}
				centerTitle={true}
				color={Colors.black}
				editBtn={true}
				searchBtn={false}
				statusBarStyle="dark-content"
				title="Topicus Events"
			/>
			<ScrollView
				contentContainerStyle={{ flexGrow: 1 }}
				refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
				style={styles.scroll}
			>
				<Card>
					{showLoginCta ? (
						<View style={styles.centeredBlock}>
							<Heading styles={styles.centerText} text={Translations.titleWelcome} />
							<Paragraph styles={styles.centerText} text={Translations.textLoginDescription} />
							<View style={styles.spacerLarge} />
							<TouchableOpacity onPress={() => navigation.navigate(Constants.screenLogin)}>
								<Cta buttonStyle="primary" label={Translations.buttonLogin} labelColor={Colors.white} upperCase={true} />
							</TouchableOpacity>
						</View>
					) : eventStore.eventData.length === 0 ? (
						<View style={styles.centeredBlock}>
							<Heading styles={styles.centerText} text={welcomeTitle} />
							<Paragraph styles={styles.centerText} text={Translations.textHomeNoEvents} />
							<View style={styles.spacerLarge} />
							<AddEvent
								label={Translations.textAddEvent}
								onQrScanPress={() => {
									navigation.navigate(Constants.screenQrScanner);
								}}
							/>
						</View>
					) : (
						<>
							<View style={styles.container}>
								<Heading text={welcomeTitle} />
								<Paragraph text={Translations.textHomeHasEvents} />
							</View>

							<View style={styles.spacerLarge} />

							<AddEvent
								label={Translations.textAddEvent}
								onQrScanPress={() => {
									navigation.navigate(Constants.screenQrScanner);
								}}
							/>

							<EventGrid />

							<View style={styles.spacerSmall} />

							<View style={styles.spacerLarge} />
						</>
					)}
				</Card>
			</ScrollView>
		</Fragment>
	);
}

export default HomeScreen;

const styles = StyleSheet.create({
	scroll: {
		flex: 1,
	},
	centeredBlock: {
		flex: 1,
		justifyContent: 'center',
		paddingHorizontal: Dimens.padding20,
	},
	centerText: {
		textAlign: 'center',
	},
	spacerSmall: {
		height: 15,
	},
	spacerLarge: {
		height: Dimens.padding25,
	},
	container: {
		paddingLeft: Platform.OS === 'ios' ? Dimens.padding20 : Dimens.padding16,
		paddingRight: Platform.OS === 'ios' ? Dimens.padding20 : Dimens.padding16,
		marginTop: Platform.OS === 'ios' ? Dimens.margin20 : Dimens.margin8,
		marginBottom: Platform.OS === 'ios' ? Dimens.margin0 : Dimens.margin5,
	},
});
